import { css, styled, Heading, TextInput, Inline, AlertDangerIcon, Feedback } from 'canoe';
import { Field, FieldAttributes, Form, Formik } from 'formik';
import { useActions, ENDPOINTS } from 'hammock';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { useAuthentication } from 'commons/hooks';
import { useSessionStorage } from 'commons/hooks/useSessionStorage';

const Container = styled.div`
  position: relative;
  width: min-content;
`;

const SizeCalibration = styled(Heading)<{ visible?: boolean; onClick?: any }>`
  ${({ theme, visible }) => css`
    font-weight: ${theme.fontWeights.bold};
    min-width: 2em;
    max-width: 100%;
    margin: 0 0 ${theme.spaces.xs};
    padding: 4px;
    height: 46px;
    white-space: pre;

    ${!visible && 'visibility: hidden;'}
  `}
`;

const StyledInput = styled(TextInput)<{ state?: 'success' | 'danger' | 'warning' }>`
  ${({ theme, state }) => css`
    font-size: ${theme.fonts.h5.fontSize};
    font-weight: ${theme.fontWeights.bold};
    padding: 2px;
    position: absolute;
    left: 0;
    top: 0;
    width: 220px;

    ${state !== 'danger' &&
    `
      width: 100%;
      background-color: transparent;
      border: 1px solid transparent;
      &:hover,
      &:focus {
        border: 1px solid ${theme.colors.border};
      }
    `};
  `}
`;

const InlineMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: -12px;
  width: 162px;
`;

const validationSchema = Yup.object().shape({
  sectionTitle: Yup.string().required('Must specify a name'),
});

type Folder = { name?: string; id?: number };

interface Props {
  initialFolder?: {
    name: string;
    id: number;
  };
  onSave?: (folder: Folder) => void;
  autofocus?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

export const FolderTitle = ({ initialFolder, onSave, autofocus, inputRef }: Props) => {
  const { create, update } = useActions(ENDPOINTS.contentFolder) as any;
  const { applicationId } = useAuthentication();
  const [, setSectionFolder] = useSessionStorage(`newSectionFolder.${applicationId}`, null);
  const [folder, setFolder] = useState<Folder>(initialFolder || {});

  const onSubmit = async (values: Record<string, string>) => {
    if (!values.sectionTitle || folder?.name === values.sectionTitle) {
      return;
    }

    if (!initialFolder) {
      const resource = await create({
        body: { name: values.sectionTitle },
        onErrorNotification: {
          message: `Something went wrong while creating the section`,
        },
        invalidates: {
          [ENDPOINTS.contentDashboard]: true,
          [ENDPOINTS.contentFolder]: true,
        },
      });

      if (resource?.id) {
        onSave?.(resource);
        setSectionFolder(resource.id);
        setFolder({
          id: resource.id,
          name: values.sectionTitle,
        });
      }
      return;
    }

    const resource = await update({
      body: { name: values.sectionTitle },
      id: folder.id,
      invalidates: {
        [ENDPOINTS.contentDashboard]: true,
        [ENDPOINTS.contentFolder]: true,
      },
    });

    onSave?.(resource);
    setFolder({
      id: resource.id,
      name: values.sectionTitle,
    });
  };

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={{ sectionTitle: folder?.name || 'New Section' }}
    >
      {({ handleSubmit }) => (
        <Form>
          <Inline>
            <Field required name="sectionTitle">
              {({ field, meta }: FieldAttributes<any>) => (
                <>
                  <Container>
                    <SizeCalibration size="XS">{field.value}</SizeCalibration>
                    <StyledInput
                      {...field}
                      ref={inputRef}
                      autoFocus={autofocus}
                      type="text"
                      onBlur={handleSubmit}
                      state={meta.touched && meta.error ? 'danger' : undefined}
                    />
                  </Container>
                  {meta.touched && meta.error && (
                    <InlineMessage>
                      <AlertDangerIcon size="s" color="Danger" />
                      <Feedback ml="xxs" color="danger">
                        {meta.error}
                      </Feedback>
                    </InlineMessage>
                  )}
                </>
              )}
            </Field>
          </Inline>
        </Form>
      )}
    </Formik>
  );
};
